import { useEffect, useMemo, useState } from 'react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { BannerType, bannerTemplateKeys } from '../../../models/BannersModels';

// Should match the fallbackLng list in configs/i18nConfig.ts
export const supportedLanguages = ['en', 'ie', 'de', 'pt'];

interface UseMessagePreviewProps {
  bannerType: BannerType | '';
  startDate: DateTime | null;
  endDate: DateTime | null;
}

function useMessagePreview({ bannerType, startDate, endDate }: UseMessagePreviewProps) {
  const [t] = useTranslation();
  const [activeLanguageIndex, setActiveLanguageIndex] = useState(0);
  const [, forceRerender] = useState(0);

  useEffect(() => {
    // Force load all of the app languages so they are all available
    i18next.loadLanguages(supportedLanguages, () => forceRerender((n) => n + 1));
  }, []);

  const activeLanguage = supportedLanguages[activeLanguageIndex];
  const languageTabs = useMemo(() => supportedLanguages.map((lang) => t(`common.languages.${lang}`)), [t]);
  const templateKey = bannerType ? bannerTemplateKeys[bannerType] : undefined;

  // Translator fixed to the active tab's language, regardless of the UI language
  const fixedT = useMemo(() => i18next.getFixedT(activeLanguage), [activeLanguage]);

  const startDateText = startDate
    ? startDate.toFormat('dd/MM/yyyy HH:mm')
    : fixedT('pages.bannerOnboarding.message.startDatePlaceholder');
  const endDateText = endDate
    ? endDate.toFormat('dd/MM/yyyy HH:mm')
    : fixedT('pages.bannerOnboarding.message.endDatePlaceholder');

  return {
    languageTabs,
    activeLanguage,
    activeLanguageIndex,
    setActiveLanguageIndex,
    fixedT,
    templateKey,
    startDateText,
    endDateText,
  };
}

export default useMessagePreview;
